import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, addDoc, getDocs, updateDoc, doc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../../../config/firebaseConfig';
import useBankAccountText from './bankAccountText';
import { ALL_GROUPS, COLUMN_KEYS, FORM_FIELDS, statusGroupOf } from './bankAccountColumns';

const lower = (value) => String(value ?? '').toLocaleLowerCase('tr-TR');

/**
 * Data of Banka Hesapları: the BankAccount records, adding and updating
 * (same Firestore calls as the old page), the search, the column filters and
 * the status quick filter.
 */
const useBankAccounts = () => {
  const { label } = useBankAccountText();
  const [bankAccounts, setBankAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({});
  const [statusGroup, setStatusGroup] = useState(ALL_GROUPS);
  const [selectedId, setSelectedId] = useState(null);

  const fetchBankAccounts = useCallback(async () => {
    setIsLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'BankAccount'));
      const accounts = querySnapshot.docs.map((docSnapshot) => ({
        id: docSnapshot.id,
        ...docSnapshot.data(),
      }));
      setBankAccounts(accounts);
    } catch (error) {
      toast.error(label('toast.loadError', { message: error.message }));
    } finally {
      setIsLoading(false);
    }
  }, [label]);

  useEffect(() => {
    fetchBankAccounts();
    // Yalnızca açılışta
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Adds a record, or updates `editingId` (the form has already checked that
   * every field is filled). Only the form's fields are written.
   */
  const saveAccount = async (formData, editingId) => {
    const data = Object.fromEntries(FORM_FIELDS.map((key) => [key, formData[key]]));
    try {
      if (editingId) {
        await updateDoc(doc(db, 'BankAccount', editingId), data);
        toast.success(label('toast.updated'));
      } else {
        await addDoc(collection(db, 'BankAccount'), data);
        toast.success(label('toast.added'));
      }
      await fetchBankAccounts(); // Refresh the list after saving
      return true;
    } catch (error) {
      console.error('Error saving data:', error);
      toast.error(editingId ? label('toast.updateError', { message: error.message }) : label('toast.saveError', { message: error.message }));
      return false;
    }
  };

  const filteredAccounts = useMemo(() => {
    const term = lower(searchTerm.trim());
    return bankAccounts
      .filter((account) => {
        if (term && !COLUMN_KEYS.some((key) => lower(account[key]).includes(term))) return false;
        if (statusGroup !== ALL_GROUPS && statusGroupOf(account.durum) !== statusGroup) return false;
        return Object.entries(filters).every(([key, value]) => !value || lower(account[key]).includes(lower(value)));
      })
      .sort((a, b) => String(a.firma || '').localeCompare(String(b.firma || ''), 'tr'));
  }, [bankAccounts, searchTerm, statusGroup, filters]);

  const stats = useMemo(() => {
    const counts = { total: bankAccounts.length, active: 0, passive: 0, pending: 0, other: 0 };
    bankAccounts.forEach((account) => { counts[statusGroupOf(account.durum)] += 1; });
    return counts;
  }, [bankAccounts]);

  const selectedAccount = useMemo(() => bankAccounts.find((account) => account.id === selectedId) || null, [bankAccounts, selectedId]);

  const handleFilter = useCallback((key, value) => setFilters((current) => ({ ...current, [key]: value })), []);
  const clearFilters = useCallback(() => {
    setFilters({});
    setSearchTerm('');
    setStatusGroup(ALL_GROUPS);
  }, []);

  return {
    bankAccounts,
    isLoading,
    filteredAccounts,
    stats,
    searchTerm,
    setSearchTerm,
    filters,
    handleFilter,
    clearFilters,
    statusGroup,
    setStatusGroup,
    selectedAccount,
    setSelectedId,
    saveAccount,
  };
};

export default useBankAccounts;
